/**
 * Context text hygiene.
 *
 * Free-text context fields (business_problem, desired_outcome, current_state,
 * volumes, incumbent notes, …) have historically absorbed two kinds of junk:
 *  1. the intent parser echoing the Screen A answer verbatim, and
 *  2. model chatter from the RAG-unpack pass ("SOFIE … RFP Strategy:
 *     Comprehensive Evaluation Framework I've analyzed your existing …")
 *     written straight into the fields — titles, narration and whole strategy
 *     blocks (## RATE & PRICING STRUCTURE) dumped verbatim.
 *
 * These helpers scrub conversational filler / title wrappers / meta-narration
 * from the input so that downstream fields contain only the user's own facts.
 * so only clean domain substance survives, and normalise the contract term to
 * a sane month value. Empty results deliberately resolve to "" so the pack
 * shows an honest `[TBD]` + gaps row instead of a verbatim transcript.
 */

/**
 * Model meta-narration openings. When one of these leads a clause we strip it
 * — together with the filler up to the first `,` / `:` / `.` — keeping any
 * genuine substance that follows: "I've analyzed your operation. The issue
 * is …" → "The issue is …".
 */
const LEAD =
  "i(?:'ve| have)? (?:analy[sz]ed|reviewed|looked at|gone through|went through|examined|read|checked)" +
  "|based on (?:my |the )?(?:analysis|review|reading|assessment)" +
  "|let(?:'s| me) (?:walk|dive|start|break|look|focus|share|give|summar[iz]e|think about)" +
  "|here(?:'s| is) (?:my |the |a )?(?:plan|strategy|take|approach|recommendation|summary|analysis|overview|thoughts?|breakdown)" +
  "|here(?:'s| is) (?:what|how|why)" +
  "|my (?:analysis|assessment|recommendation|approach|take|comments?) (?:is|are|focuses|focused)(?: on)?" +
  "|in summary|to summar[iz]e|to wrap up|in conclusion|in closing" +
  "|as (?:i|we|you) (?:mentioned|noted|discussed|said)" +
  "|as requested|please note|note that" +
  // Prompt-artifact preamble: "Your current MDS agreement (2022-2023) reveals
  // several pain points …" is meta-framing repeated from the prompt, not new
  // substance. The lead cuts through the next `:` / `,` / `.` boundary, so a
  // preamble that opens a colon-list ("reveals several pain points … : vague
  // 'open-book' pricing …") keeps the real content that follows it.
  "|your current [^.,:;]{0,40}?\b(?:agreement|contract|operation|model|pricing)\b[^.,:;]{0,40}?(?:reveals?|uncovers?|highlights?|shows?|exposes?)\\b";

/** Non-anchored so a meta lead is found even mid-segment (after a title). */
const LEAD_ANYWHERE = new RegExp(`\\b(${LEAD})`, "i");

/** Words that mark a line as a document title rather than a sentence. */
const TITLE_CUE =
  /\b(?:rfp|rfq|procurement|strategy|framework|evaluation|assessment|analysis|overview|plan|roadmap|proposal|readiness|scope|background|approach|summary|design|baseline|benchmark|compliance|program|operations|solution|project|brief|workshop|initiative|white ?paper|edition)\b/i;

/**
 * Normalise rich-bullet / strategy-dump text into newline-separated
 * candidates so downstream sentence splitters can handle it.  Converts
 * `• `, `✓ ` bullets and `; ` list separators into newlines.  Also adds
 * newlines after `:` that follow known header phrases ("Issues:", "Gap:"
 * etc.) so header text doesn't merge with the content that follows.
 */
export function normalizeRichBullet(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    // Bullets → newline
    .replace(/•\s*/g, "\n")
    .replace(/✓\s*/g, "\n")
    // Semicolons before uppercase/digit/list markers → newline
    .replace(/;\s+(?=[A-Z\d✓•])/g, "\n")
    // Colons after header-like phrases → newline
    .replace(/(?<=\b(?:Issues?|Contract|Agreement|Gap|Gaps|RISK|Demand|Framework|Structure|Comparison|Commitment|Responsiveness|Differentiation|Treatment)\b)\s*:\s*/gi, ":\n")
    // For dense paragraphs (no sentence-ending punctuation), add newlines
    // between space-separated capital-initial phrases.  Only fires when a
    // line exceeds 200 chars without any `.!?` sentence break — the
    // heuristic for "compressed strategy dump" text.
    .split("\n")
    .map((line) => {
      if (line.length > 200 && !/[.!?]\s/.test(line)) {
        return line.replace(/([a-z.)])\s+(?=[A-Z][a-z])/g, "$1\n");
      }
      return line;
    })
    .join("\n")
    // Collapse doubled separators left by the splits.
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Metadata labels that mark a line as transport / provenance scaffolding
 * rather than draft substance. When one of these opens a line we drop the
 * whole line — "Prepared by: …", "Timestamp: …" and friends are routing
 * metadata, never bidder-facing facts.
 */
const METADATA_LABEL =
  /^(?:(?:prepared|initiated|created|reviewed|updated|issued|sent|received|logged|audited|approved|owned|attended|entered)\s+(?:by|for)\b|(?:owner|status|version|timestamp|date|time|subject|topic|submitted|transmitted|distribution|classification|document(?:\s+id|\s+name|\s+number|\s+title)?|file\s+name)\b\s*[:：]|(?:meeting|minutes|briefing|agenda|session|notes|highlights|summary)\s+(?:notes|minutes|summary|highlights|points|of)\s*[:：])/i;

/**
 * Strip provenance noise from an ingested draft before any narrative field is
 * populated (Rule 1). Removes:
 *  - raw section dividers (`---`, `===`, `___`, `~~~`) on their own line,
 *  - metadata-label lines ("Prepared by: …", "Timestamp: …", "Status: …"),
 *  - inline ISO timestamp tokens ("2026-08-10 14:30", "2026-08-10T14:30:00Z").
 * Dates and content sentences are preserved — only scaffold is removed, so
 * "- - -" separators or an email-style footer can never bleed into Section 1.
 */
export function stripIngestNoise(raw: string | null | undefined): string {
  if (!raw) return "";
  return String(raw)
    .split(/\r?\n/)
    .map((line) => {
      if (/^\s*(?:-{3,}|={3,}|_{3,}|~{3,}|[*•]{3,})\s*$/.test(line)) return "";
      if (/^\s*(?:#+)\s*$/.test(line)) return "";
      if (METADATA_LABEL.test(line.trim())) return "";
      return line.replace(/\b\d{4}-\d{1,2}-\d{1,2}[T ]\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/gi, "");
    })
    .filter((line) => line.trim().length > 0)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Titles are Title-case labels ("SOFIE … RFP Strategy: Evaluation Framework"). */
function isTitleLike(text: string): boolean {
  const p = text.trim();
  if (!p || p.length > 160) return false;
  const words = p.split(/\s+/).filter(Boolean);
  if (words.length < 3) return false;
  if (!TITLE_CUE.test(p)) return false;
  // A real sentence has several lower-case-initial words ("Here's the roadmap:
  // Q1 network design …"); a heading does not.
  if (words.filter((w) => /^[a-z]/.test(w)).length >= 3) return false;
  return true;
}

/**
 * Manuscript skeleton left before a mid-segment lead: all-caps/all-title junk
 * with no lower-case letters ("EXECUTIVE SUMMARY", "PART 1"). Recognised even
 * when newline-collapse shrinks it below the 3-word title threshold.
 */
function isSkeletonish(text: string): boolean {
  const t = text.trim();
  return t.length > 0 && t.length <= 90 && /^[A-Z0-9&'’./:,[\]()@\-—–\s\n]+$/.test(t);
}

/** Announcing-preamble sentences ("Here's an enterprise-grade framework…"). */
const HERE_PREFACE = /^here(?:'s| is)\b/i;

/** Cues that mark the announcing sentence as a document response, not content. */
const PREFACE_CUE =
  /\b(?:framework|blueprint|model|guide|proposal|response|document|outline|walkthrough|appendix|report|edition|presentation|deliverable)\b|enterprise-grade|capital-efficient|to drive|to deliver|to support your|for the (?:rfp|request)|that (?:outlines|addresses)|what you need|everything (?:you|we|you'll)/i;

/**
 * A "here's …" sentence that announces a deliverable (framework, proposal,
 * response) rather than states domain facts. We cut everything through the
 * next `.` `:` `,` `;` — so "Here's an enterprise-grade framework … for the
 * RFP. Fragmentation …" keeps the real problem sentence, and a colon case
 * ("Here's a report of volumes by site: 120k units/month") keeps the tail.
 * "Here is the volumetrics summary for both options." has no cue and survives.
 */
function isPreface(segment: string): boolean {
  const t = segment.trim();
  if (!HERE_PREFACE.test(t)) return false;
  return PREFACE_CUE.test(t.slice(0, 160));
}

/**
 * Strip a lead clause from a sentence segment, keeping anything that follows
 * a `,` `:` `;` or `.` — the point where substance starts. A segment that is
 * all narration ("my recommendation is to …") becomes "". When a segment is a
 * lone document title with no lead, it is dropped too.
 */
function stripLeadSegment(segment: string): string {
  const raw = segment.replace(/^\s+/, "") as string;
  if (!raw) return "";
  if (isPreface(raw)) {
    const after = raw.replace(HERE_PREFACE, "");
    const b = /[,:;.]/.exec(after);
    return b ? stripLeadSegment(after.slice(b.index + 1)) : "";
  }
  const m = LEAD_ANYWHERE.exec(raw);
  if (!m) return isTitleLike(raw) ? "" : raw;

  // An inner lead (not at the start) only cuts when everything before it is
  // title-like — "Framework I've analyzed …" — never a real sentence.
  const leadAt = m.index;
  const preamble = raw.slice(0, leadAt);
  if (preamble.trim() && !isTitleLike(preamble) && !isSkeletonish(preamble)) return raw;

  const rest = raw.slice(leadAt + m[0].length);
  const boundary = /[,:.;]/.exec(rest);
  let kept: string;
  if (boundary) {
    kept = rest.slice(boundary.index + 1);
  } else {
    // No comma/colon/period: the remainder is narration ("through the
    // operation", "your existing network") unless it opens a fresh
    // substantive clause (capitalised sentence or bullet) — keep those.
    const t = rest.replace(/^\s+/, "");
    kept = /^(?:[-*]|(?=[A-Z0-9]))/.test(t) ? t : "";
  }
  return kept.replace(/^\s+/, "").replace(/\s+$/, "");
}

/** Keep stripping leads until the segment is stable (lone title → ""). */
function stripLeadLoop(part: string): string {
  let cur = part;
  for (let n = 0; n < 5; n++) {
    const next = stripLeadSegment(cur);
    if (!next || next === cur) return next;
    cur = next;
  }
  return cur;
}

/** Mid-sentence glue phrases that can sit beside real content. */
const CHATTER_INLINE =
  /\b(?:as (?:i|we|you) (?:mentioned|noted|discussed|said above|noted earlier)|as i was saying|just to (?:clarify|add)|let me know (?:if|whether)|if you (?:have|need) (?:any further |any )?(?:questions?|concerns|feedback)|please don'?t hesitate (?:to )?|hope (?:this|that) helps|glad to help|feel free to (?:reach |contact ))\b\.?/gi;

/** Trailing pleasantries / sign-offs. */
const SIGNOFF = /(?:thanks?|thank you|regards|best(?: regards)?|cheers|kind regards|with warm regards)[.,!-]?.*$/im;

/** Standalone meta heading lines the model emits ("## Strategy", "**Analysis:**"). */
const META_HEADING =
  /(?:^|[.!?])\s*(?:#{1,6}|\*{1,2})\s*(?:\w+\s+)?(?:analysis|recommendation|strategy|approach|conclusion|findings|key takeaways|key points|summary|overview|next steps)s?[ \t]*(?::[ \t]*)?$/gim;

/** Inline meta labels at a line start ("**Recommendation:** Consolidate …"). */
const META_LABEL = /(?:^|\n)\s*[*_]{1,2}\s*(?:analysis|recommendation|strategy|approach|conclusion|findings|key takeaways|summary|overview|next steps)s?[*_]{0,2}\s*[:,]\s*/gi;

/** Generic strategy-dump headings ("## 1. BASELINE", "# SOFIE RFP Strategy"). */
const GENERIC_HEADING = /(?:^|\n)\s*#{1,6}\s+(?=[^#\n])[^\n]{1,120}$/gm;

/** Table / horizontal-rule rows from strategy dumps. */
const TABLE_ROWS = /(?:^|\n)\s*(?:\|.*\|\s*$|---+|====+)/gm;

/**
 * ALL-CAPS section headers ("RATE & PRICING STRUCTURE", "Q4 FY2027 VOLUMES").
 * Word-based (a regex would break on "&" / "/"): every token must be all-caps
 * and there must be several — "98.5% OTIF" (2 tokens) stays as a legit KPI.
 * Leading prompt punctuation (": CRITICAL GAPS & OPPORTUNITIES") is ignored.
 */
function isAllCapsHeading(line: string): boolean {
  const l = line.trim().replace(/^[:\s;#>*-]+/, "").replace(/[*:;.!]+\s*$/, "").trim();
  if (!l || l.length < 14 || l.length > 130) return false;
  const tokens = l.split(/[\s&/\-–—]+/).filter(Boolean);
  if (tokens.length < 3) return false;
  return tokens.every((t) => /^[A-Z0-9][A-Z0-9.'’*%]*$/.test(t));
}

/**
 * Inline ALL-CAPS header fragments the model copies straight from the prompt
 * (": CRITICAL GAPS & OPPORTUNITIES — …"). Requires two or more ALL-CAPS
 * tokens immediately followed by a colon or dash, so real sentences and
 * acronym lists ("OTIF and KPI …") survive.
 */
const INLINE_YELLING =
  /(^|[:\s—–-])([A-Z][A-Z0-9&'’./]+(?:\s+[A-Z][A-Z0-9&'’./]+)(?:\s+[A-Z][A-Z0-9&'’./]+)*)(?=\s*[:—–-])/gm;

/**
 * Document-skeleton headers emitted by the model ("EXECUTIVE SUMMARY", "PART
 * 1", "TABLE OF CONTENTS", "REVISION 1.2", "PREPARED FOR"). Case-sensitive so
 * only the all-caps variants match — lower-case "part 1 of the schedule" is a
 * real sentence. Applied both as whole lines and inline mid-paragraph.
 */
const SKELETON_LINE =
  /(?:^|\n)\s*(?:EXECUTIVE SUMMARY|EXECUTIVE OVERVIEW|TABLE OF CONTENTS|LIST OF (?:TABLES|FIGURES)|KEY TAKEAWAYS|PREPARED (?:FOR|BY)|REVISION\s*[0-9.]+|[AP]ENN?DIX\s+[A-Z0-9]+|PART\s+(?:ONE|TWO|THREE|FOUR|FIVE|SIX|[IVXLCD]+|[0-9]{1,2})(?:\s*[:—–-].*)?|SECTION\s+[IVXLCD0-9]+(?:\s*[:—–-].*)?)\s*$/gm;

const SKELETON_INLINE =
  /\b(?:EXECUTIVE SUMMARY|EXECUTIVE OVERVIEW|TABLE OF CONTENTS|KEY TAKEAWAYS|PREPARED FOR|PART\s+(?:ONE|TWO|THREE|FOUR|FIVE|SIX|[IVXLCD]+|[0-9]{1,2})|SECTION\s+[IVXLCD0-9]+|[AP]ENN?DIX\s+[A-Z0-9]+|CRITICAL\s+GAPS\s*&\s*OPPORTUNITIES)\b/gi;

/** Drop strategy-format junk rows: headings, ALL-CAPS headers, tables/rules. */
function stripDumpRows(s: string): string {
  s = s.replace(META_HEADING, "\n").replace(META_LABEL, "\n");
  s = s.replace(GENERIC_HEADING, "\n").replace(TABLE_ROWS, "\n").replace(SKELETON_LINE, "\n");
  s = s
    .split("\n")
    .map((line) => (isAllCapsHeading(line) ? "" : line))
    .join("\n");
  // Inline prompt-header fragments (": CRITICAL GAPS & OPPORTUNITIES — …").
  s = s.replace(INLINE_YELLING, "$1");
  return s;
}

const MAX_CHARS = 600;

/** Strip conversational meta-narration, leaving clean domain substance. */
export function sanitizeContextText(raw: string | null | undefined): string {
  if (raw == null) return "";
  let s = String(raw);

  // Normalise whitespace only — keep real line structure (bullets/notes).
  s = s.replace(/\r\n?/g, "\n").replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  if (!s || /^\[tbd\]$/i.test(s)) return "";

  // Protect decimal points ("$3.00", "0.3%", "98.5%") so the sentence-boundary
  // split below never treats them as sentence enders.
  s = s.replace(/(\d)\.(\d)/g, "$1@DOTPR@$2");

  // Remove trailing pleasantries / sign-offs.
  s = s.replace(SIGNOFF, "");

  // Drop strategy-format junk: heading lines, ALL-CAPS section headers, tables.
  s = stripDumpRows(s);

  // Remove residual mid-sentence glue.
  s = s.replace(CHATTER_INLINE, " ");

  // Strip mere narration clauses + title wrappers sentence-by-sentence.
  s = s
    .split(/([.!?]+)\s*/)
    .map((part, i) => (i % 2 === 1 ? part : stripLeadLoop(part)))
    .join("")
    .replace(/([.!?])\s*([.!?])/g, "$1 $2")
    // The sentence-split above consumes the space after a sentence-ending
    // period ("Contract. Liability" → "Contract.Liability"). Re-insert a
    // space after a real sentence ender — an exclamation/question mark, or a
    // period followed by a capital letter / opener. Decimals ("98.5%") and
    // abbreviations are untouched because a digit or lower-case letter
    // follows the period there.
    .replace(/([.!?])(?=[A-Z$£("['{])/g, "$1 ")
    .replace(SKELETON_INLINE, " ");

  // Drop lines that are now empty / meta-only cruft.
  s = s.split("\n")
    .map((line) => line.replace(/^[-*>\s]+/, "").trim())
    .filter((line) => line && /[A-Za-z0-9]/.test(line))
    .join("\n");

  // Trim leading punctuation where a chatter phrase used to be.
  s = s.replace(/(^|\n)[:,;.]+/g, "$1");

  s = s.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").replace(/[*_`]+/g, "").trim();
  // Collapse doubled separators left by stripped segments ("..", ". .") and
  // drop any leading punctuation the strip could leave (".The key problem…").
  s = s.replace(/[.!?](?:\s*[.!?])+/g, ". ").replace(/^[\s.,;:!?—–-]+/, "").trim();
  if (!s || /^[-*\s]+$/.test(s)) return "";

  // Cap runaway blocks to a short structured summary.
  if (s.length > MAX_CHARS) {
    const cut = s.slice(0, MAX_CHARS);
    const lastBreak = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("\n"));
    s = lastBreak > 200 ? cut.slice(0, lastBreak + 1) : cut;
  }
  return s.replace(/@DOTPR@/g, ".").trim();
}

/** Unit words that indicate real throughput figures in a strategy dump. */
const VOLUME_UNITS =
  /units?|pcs?|pieces?|pallets?|cartons?|cases?|orders?|shipments?|parcels?|skus?|lines?|kgs?|kg\b|cbm|m3|tonnes?|tons?|sqm|sq\s*ft|ftes?|vehicles?|containers?|sites?|dcs?|locations?|warehouses?|facilities?/i;

/**
 * Discount / volume-tier bands from a pricing block: "Baseline: 500",
 * "Tier 1: 501 - 750" → "Baseline: 500; Tier 1: 501–750". Returns [] when the
 * text has no banded structure, so callers fall back to plain metrics.
 */
function extractVolumeTiers(s: string): string[] {
  const tiers: string[] = [];
  // Handles "Baseline: 500", "Tier 1: 501–750", "Tier 2: 751 - 1,000",
  // "Tier 3: >1000" (open-ended), "500+", and optional discount suffixes
  // like "→ 3–5% discount".
  const tierRe =
    /\b(baseline|tier\s*[0-9]{1,2}|band\s*[0-9]{1,2}|volume\s*[0-9]{1,2})\s*[:=–—-]?\s*([>≥]?\s*)?([0-9][0-9,.]*)\s*(?:([+]?)(?:\s*(?:-|–|—|to|by)\s*([0-9][0-9,.]*))?)(?:\s*[→➜>]\s*([0-9][0-9,.]*)\s*%)?(?=[+\s.,;:\])]|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = tierRe.exec(s)) && tiers.length < 8) {
    const label = /baseline/i.test(m[1])
      ? "Baseline"
      : m[1].replace(/^tier/i, "Tier").replace(/^band/i, "Band").replace(/^volume/i, "Volume");
    const openEnded = m[2] ? "+" : m[4] ? "+" : "";
    const band = `${m[3].replace(/,/g, "")}${m[5] ? `–${m[5].replace(/,/g, "")}` : `${openEnded}`}`;
    const discount = m[6] ? ` → ${m[6]}%` : "";
    const row = `${label}: ${band}${discount}`;
    if (!tiers.includes(row)) tiers.push(stripTierDiscounts(row));
  }
  return tiers;
}

/**
 * Reduce a (possibly raw strategy-dump) volumes string to a concise,
 * metric-first summary line — "RATE & PRICING STRUCTURE …" becomes
 * "120,000 units/month; 4 sites". Falls back to a short cleaned sentence.
 */
export function summarizeVolumes(raw: string | null | undefined): string {
  if (raw == null) return "";
  let s = String(raw)
    .replace(/\r\n?/g, "\n")
    .replace(/\t+/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
  if (!s || /^\[tbd\]$/i.test(s)) return "";

  // Drop strategy-format junk first.
  s = stripDumpRows(s);

  // Banded pricing block ("Baseline: 500", "Tier 1: 501 - 750") wins over a
  // free-form metric line — it is the structured answer the draft states.
  const tierRows = extractVolumeTiers(s);
  if (tierRows.length) return tierRows.join(", ");

  // Fold to sentence stream, strip narration/title wrappers.
  s = s.replace(/\n+/g, ". ").replace(/\s+/g, " ").trim();
  s = s
    .split(/([.!?]+)\s*/)
    .map((part, i) => (i % 2 === 1 ? part : stripLeadLoop(part)))
    .join("")
    .replace(/([.!?])\s*([.!?])/g, "$1 $2")
    .replace(SKELETON_INLINE, " ");

  // Metric-first: pull the throughput figures (number + volume unit [+/period]).
  const metricRe = new RegExp(
    `\\b([0-9][0-9,.kmt]*)\\s*(${VOLUME_UNITS.source})(?:\\s*(?:\\/|per)\\s*)?(month|mth|mo|year|yr|week|day|annum|pa)?`,
    "gi",
  );
  // Count metrics with a unit separated by a word: "8 radiopharmacy sites".
  const countRe =
    /\b([0-9][0-9,.kmt]*)\s+(?:[A-Za-z.][\w.’']* )?(sites?|dcs?|warehouses?|locations?|facilities?|vehicles?|containers?|ftes?)\b/gi;
  const metrics: string[] = [];
  const push = (num: string, unit: string, per = "") => {
    const token = `${num} ${unit.toLowerCase()}${per}`;
    if (!metrics.includes(token)) metrics.push(token);
  };
  let m: RegExpExecArray | null;
  while ((m = metricRe.exec(s)) && metrics.length < 6) push(m[1], m[2], m[3] ? `/${m[3].toLowerCase()}` : "");
  while ((m = countRe.exec(s)) && metrics.length < 6) push(m[1], m[2]);
  if (metrics.length) return metrics.join("; ");

  // Fallback: concise single-line summary of the cleaned text.
  const clean = s.replace(/[;,:]+$/g, "").replace(/\s+/g, " ").trim();
  return clean.length > 140 ? clean.slice(0, 140) + "…" : clean;
}

/**
 * Normalise a contract-term candidate to a plausible month count.
 *
 * Accepts bare numbers (24), "N months", "N years" and text that mentions a
 * term ("24 to 12 months" → base 24). Spelled-out numbers ("two years",
 * "twelve months") are converted first. Rejects dates ("Dec 2026", "Q1 2027"),
 * concatenated junk ("2412"), and values outside the plausible 6–120 range,
 * returning null so the pack renders an honest [TBD].
 */
export function normalizeContractTerm(v: unknown): number | null {
  if (v == null) return null;
  let raw = String(v).trim().toLowerCase();
  if (!raw || raw === "[tbd]") return null;

  // Spelled-out numbers → digits ("two years" → "2 years", "twenty-four" → "24").
  // Compound entries must precede single-word ones so "twenty four" wins.
  const NUMBER_WORDS: Record<string, string> = {
    "twenty four": "24", "twenty-four": "24", "twentyfour": "24",
    "twenty one": "21", "twenty-two": "22", "twenty three": "23",
    eighteen: "18", twenty: "20", twelve: "12", eleven: "11",
    ten: "10", nine: "9", eight: "8", seven: "7", six: "6",
    five: "5", four: "4", three: "3", two: "2", one: "1",
  };
  for (const [w, d] of Object.entries(NUMBER_WORDS)) {
    raw = raw.replace(new RegExp(`\\b${w.replace(/ /g, "\\s+")}\\b`, "g"), d);
  }
  if (!raw) return null;

  // Years → months ("2 years", "3-year term"). Leftover smaller time words
  // ("2 years 0 months") fold in rather than killing the years match.
  const yrs = /([0-9]{1,2}(?:\.5)?)\s*[-–—]?\s*(?:years?|yrs?)\b/.exec(raw);
  if (yrs) {
    let n = Number(yrs[1]) * 12;
    const extra = /(\d{1,2})\s*mo(?:nth)?s?\b/.exec(raw.replace(yrs[0], ""));
    if (extra) n += Number(extra[1]);
    return n >= 6 && n <= 120 ? n : null;
  }

  // A value that is only a digit run must be a bare term — reject 4+ digit junk.
  if (/^\d{1,3}$/.test(raw)) {
    const n = Number(raw);
    return n >= 6 && n <= 120 ? n : null;
  }

  // Reject date/deadline-like strings (no month/year duration cue).
  const isDuration = /mo(?:nth)?s?\b|\byears?\b/.test(raw);
  const looksLikeDate = /\b(?:19|20)\d{2}\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*|(?:^|\D)\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|(?:^|\s)q[1-4]\b/.test(raw);
  if (looksLikeDate && !isDuration) return null;

  // First plausible number wins ("24 to 12 months" → 24; "36" → via cue).
  const tokens = raw.split(/[^0-9.]+/).filter(Boolean);
  for (const tok of tokens) {
    if (tok.includes(".")) continue;
    const n = Number(tok);
    if (n >= 6 && n <= 120) return n;
  }
  return null;
}

/** "24" → "24 months", null → null. */
export function contractTermLabel(v: number | null | undefined): string | null {
  return v ? `${v} months` : null;
}

/** US state codes the site extractor accepts (avoids false positives). */
const US_STATES = new Set([
  "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "HI",
  "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN",
  "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH",
  "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA",
  "WI", "WV", "WY",
]);

/** Common sentence-start words that would otherwise fake a city before a code. */
const SITE_STOP_WORDS = new Set([
  "please", "thank", "best", "note", "the", "our", "your", "from", "with",
  "this", "that", "per", "for", "king", "san", "saint", "port", "mount",
  "new", "north", "south", "east", "west",
]);

/**
 * Pull "City XX" site entries out of draft text ("Sterling VA, Totowa NJ").
 * Two passes: comma-form ("Sterling, VA") allows multi-word cities; plain
 * "City XX" keeps a single-token city so "Sterling VA Totowa NJ" can't merge.
 * State codes must be known US states. Returns entries like "Sterling VA".
 */
export function extractSiteLocations(text: string | null | undefined): string[] {
  if (text == null) return [];
  const t = String(text).replace(/,\s*,\s*/g, ", ");
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (m: RegExpExecArray) => {
    const city = m[1].trim().replace(/^[,：:;.\-—]+/, "").trim();
    const code = m[2];
    if (!city || !US_STATES.has(code)) return;
    if (SITE_STOP_WORDS.has(city.toLowerCase())) return;
    if (/\b(?:warehouse|dc|facility|plant|site|distribution|hub|site)\b/i.test(city.toLowerCase())) return;
    const site = `${city} ${code}`;
    if (seen.has(site)) return;
    seen.add(site);
    out.push(site);
  };

  let m: RegExpExecArray | null;
  const commaRe = /\b([A-Z][A-Za-z.'’\-]+(?:[ -]+[A-Z][A-Za-z.'’\-]+)*)\s*(?:,\s*|:\s*)([A-Z]{2})\b/g;
  while ((m = commaRe.exec(t)) && out.length < 12) push(m);
  const spaceRe = /\b([A-Z][A-Za-z.'\-]+)\s+([A-Z]{2})\b/g;
  while ((m = spaceRe.exec(t)) && out.length < 12) push(m);
  // Third pass: site-distribution patterns that omit state codes entirely
  // ("14 in Chicago, 9 in Houston, 5 in Denver", "2 sites in San Jose").
  // Cities are captured whole (never truncated), deduped against the passes
  // above, and gate on a count + "in" so casual sentence prose can't bind.
  const distRe = /\b(\d{1,4})\s+in\s+([A-Z][A-Za-z'’\-]+(?:\s+[A-Z][A-Za-z'’\-]+)*)/g;
  let cm: RegExpExecArray | null;
  while ((cm = distRe.exec(t)) && out.length < 12) {
    const city = cm[2].trim();
    const tail = t.slice(cm.index + cm[0].length).trimStart();
    if (!city || city.toLowerCase().length < 3 || SITE_STOP_WORDS.has(city.toLowerCase())) continue;
    if (/\b(?:warehouse|dc|facility|plant|site|distribution|hub|vehicles?|drivers?|units?|staff)\b/i.test(city.toLowerCase())) continue;
    // Boundary: the captured run must end at a list separator / terminator, so
    // "… $3.14 in Chicago"? or "per 1 in 5 deliveries" style prose can't bind.
    if (!/^[,;.()]|\band\b|\s*$/.test(tail)) continue;
    const site = city;
    const cityKey = city.toLowerCase();
    if (seen.has(cityKey + " (no state)")) continue;
    // Do not re-add a city already captured with a state code ("Dallas TX").
    if (out.some((s) => s.split(" ")[0].toLowerCase() === cityKey)) continue;
    seen.add(cityKey + " (no state)");
    out.push(site);
  }
  return out;
}

/**
 * Truncate text to `max` chars without leaving a dangling word or unclosed
 * quote.  The cut backs off to the nearest space, then strips a trailing
 * unmatched single- or double-quote so the output never ends with "...vague '".
 */
export function safeTruncate(text: string, max: number): string {
  if (text.length <= max) return text;
  let cut = text.slice(0, max);
  // Back off to the nearest space so we never slice mid-word.
  const sp = cut.lastIndexOf(" ");
  if (sp > max * 0.6) cut = cut.slice(0, sp);
  // If the kept portion contains an odd number of a given quote character
  // (i.e. an opening quote was started but never closed), drop the trailing
  // fragment that includes the orphaned quote so we don't end with "...vague '".
  for (const q of ['"', "'"]) {
    const n = (cut.split(q).length - 1);
    if (n % 2 === 1) {
      const idx = cut.lastIndexOf(q);
      if (idx >= 0) cut = cut.slice(0, idx);
    }
  }
  return cut.trimEnd() + "…";
}

/** Interrogatives / articles / empty-fillers that can never be an entity name
 * (a draft phrase like "Who currently provides …" must not bind the buyer as
 * "Who"). Accepted company names pass through untouched. */
const ENTITY_PLACEHOLDERS =
  /^(who|whom|whose|what|which|when|where|why|how|the|a|an|this|that|these|those|it|its|he|she|they|we|you|i|we|us|them|both|all|any|each|either|neither|none|never|nobody|some|tbd|n\/?a|na|n\/c|n\/s|etc)$/i;

/**
 * Sanitize a candidate buyer / supplier entity name for binding into meta.
 * Returns the cleaned name, or "" when the candidate is clearly not an
 * entity (interrogative, article, placeholder, or pure punctuation). Used
 * globally wherever a client / buyer name is derived so a draft's
 * "Who …" phrasing can never ship as the buyer across the cover, Section 3,
 * Section 11 and headers.
 */
export function sanitizeBuyerName(text: string | null | undefined): string {
  if (text == null) return "";
  const t = String(text)
    .replace(/["'`*_]+/g, "")
    .replace(/\s+/g, " ")
    .replace(/[;:.)]+$/, "")
    .trim();
  if (t.length < 2) return "";
  if (ENTITY_PLACEHOLDERS.test(t)) return "";
  // A multi-word candidate that OPENS with an interrogative can never be an
  // entity ("Who SOFIE RadioPharmacy needs …" binding "Who SOFIE
  // RadioPharmacy") — reject whole phrases led by a question word.
  const firstWord = /^[A-Za-z]+/.exec(t)?.[0] ?? "";
  if (/^(who|whom|whose|what|which|when|where|why|how)$/i.test(firstWord)) return "";
  return t.slice(0, 120);
}

// ─── Success-Measures intent classification ────────────────────────────────────

/**
 * Classify a briefing point for the Success-Measures rule. Section 1 must only
 * ever carry PERFORMANCE-based, vendor-measurable KPIs; anything financial,
 * penal, or liability-bearing is routed to Section 7 (Commercial) or Section 11
 * (Contract Terms). Classification is:
 *
 *   1. LIABILITY  — caps, insurance, indemnity, damage/loss exposure,
 *                   "no distinction between handling" clauses. Highest priority
 *                   so "Liability capped at $100/dose" never counts as a KPI
 *                   even though it contains a number.
 *   2. COMMERCIAL — financial structures: $/unit rates, surcharge rules, fees,
 *                   markups, rebates/discounts, penalty math, payment terms,
 *                   cost-recovery formulas.
 *   3. OPERATIONAL — performance metrics: OTIF %, on-time %, error rates,
 *                   turnaround time, temperatures, condition integrity,
 *                   delivery windows, throughput / claims-resolution cycles.
 * Returns null when none of the signals are present.
 */
const LIABILITY_CUE =
  /\bliabilit|\bcapped\s+at|\bcaps?\b.*?(?:liabilit|damage|loss|claim)|\b(?:indemnif|indemnity)|\binsurance\s+(?:minimum|cover|policy|levels?|requirements?)\b|\bno\s+distinction\b|\b(?:loss|damage)(?:es)?\s+exposure\b|\bat\s+risk\b/i;
const COMMERCIAL_CUE =
  /[$£€]\s?[0-9]|\bper\s+(?:mile|shipment|dose|min|hour|pallet|case|unit|route|kb|cbm)\b|\bsurcharge\b|\bfee\b|\bfees?\b|\bmark\s?[- ]?up\b|\bmarkup\b|\brebates?\b|\bdiscount\b|\brate\s+card\b|\bwait\s+time\s+charges?\b|\btechnician\s+return\b|\bmileage\s+rate\b|\bfuel\s+surcharge\b|\bpenalt|\bpayment\s+terms?\b|\bnet\s+[0-9]+\s+days\b|cost[- ]recovery|replacement\s+(?:cost|value)|\bcost[- ]reflective\b|\bprices?\s*(?:valid|fixed)?\b.*\bmonths?\b|\bactual\s+cost\b|\bcost\s*\+|\bcost[- ]plus\b|\badmin(?:istrative)?\s*(?:fee|charges?|costs?|markup|overheads?)\b|\baccessorials?\b|\bhandling\s+charges?\b/i;
const OPERATIONAL_CUE =
  /\botif\b|\bon[- ]?time\b|\bstat\b|temperature|condition integrity|% of deliveries|\d+\s*%|\bdelivery\s+window\b|\berror\s+rate\b|\bturnaround\b|\bthroughput\b|dead on arrival|\bclaims?\s+resolution\b|quality\s+metric|compliance\s+rate\b|recall\b/i;

export function classifyBriefPoint(raw: string | null | undefined): "operational" | "commercial" | "liability" | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (LIABILITY_CUE.test(s)) return "liability";
  if (COMMERCIAL_CUE.test(s)) return "commercial";
  if (OPERATIONAL_CUE.test(s)) return "operational";
  return null;
}

// ─── ISSUE 1: trigger → Section 1 synthesis ────────────────────────────────────

export interface WhyContextSynthesis {
  current_state: string;
  current_state_constraints: string;
  business_problem: string;
  desired_outcome: string;
}

/**
 * Deterministically expand a brief "why are we running this RFP?" trigger (with,
 * optionally, the goodLooks answer) into the four Section 1 prose fields instead
 * of shipping `[TBD]` rows. Only template framing is synthesised; the buyer's
 * words are woven in thematically (no raw verbatim echo / parenthetical
 * duplication) and no fact or number is fabricated — measurable success targets
 * come from the category SLA catalogue, never from this synthesizer. Returns
 * empty strings when there is no trigger to work from.
 */
export function synthesizeWhyContext(why: string | null | undefined, goodLooks: string | null | undefined): WhyContextSynthesis {
  const w = sanitizeContextText(String(why ?? "")).trim().replace(/[.\s]+$/, "");
  const g = sanitizeContextText(String(goodLooks ?? "")).trim().replace(/[.\s]+$/, "");
  const src = `${w} ${g}`;
  if (!w) {
    return { current_state: "", current_state_constraints: "", business_problem: "", desired_outcome: "" };
  }

  const perf = /\botif\b|on[- ]?time|sla\b|service[- ]level|performance|missed|late\b|degrad|deteriorat|fail|can('|’)?t|cannot|unable/i.test(src);
  const volume = /volume|demand|growth|increase|surge|capacity|ramp|scale/i.test(src);
  const cost = /cost|budget|\bpric(?:ing|e|es)?\b|saving|cheaper|expensive/i.test(src);
  const slaTargets = perf ? "the required On-Time In-Full (OTIF) and service-level targets" : "the stated service-level targets";
  const volumeClause = volume ? "significant volume increases" : "the stated demand";
  const demandClause = volume ? "amidst surging demand" : "under the stated operating conditions";

  const constraints = [
    volume ? "lack of scalable capacity" : "limited capacity to absorb demand changes",
    perf ? "weak SLA enforcement and deteriorating baseline performance" : "weak service-level performance discipline",
    volume ? "inability to absorb volume increases without service degradation" : "inflexibility to adapt as operating conditions change",
  ];

  const fix = `Establish a robust, scalable vendor partnership capable of supporting ${volumeClause} while consistently meeting ${slaTargets}${cost ? " at a competitive cost" : ""}`;

  return {
    current_state: `The current operations rely on incumbent service providers who are struggling to maintain baseline performance and SLAs ${demandClause}.`,
    current_state_constraints: `${constraints.join(", ")}.`,
    business_problem: `Existing provider arrangements cannot maintain ${slaTargets} while handling ${volumeClause}, creating operational bottlenecks.`,
    desired_outcome: g ? `${fix}. ${g}.` : `${fix}.`,
  };
}

// ─── Internal note detection / Ask-question routing ────────────────────────────

/**
 * True when a sentence is an internal negotiation tactic or strategy note that
 * must never reach Section 1 or the main RFP body.  Two classes:
 *
 * 1. "Ask: …" / "Question N: …" lines → routed to Appendix L (Vendor
 *    Questionnaire).
 * 2. Pricing-rebate formulas ("→ 3–5% discount", "internal rebate formula")
 *    → dropped outright.
 */
export function isInternalNote(raw: string): boolean {
  const s = raw.trim();
  if (/^ask\b|^question\b/i.test(s)) return true;
  // Arrow + discount suffix ("→ 3–5% discount") or bare "% discount".
  if (/[→➜>]\s*[-–—]?[0-9][0-9.,]*\s*%/i.test(s) || /%\s*(?:discount|saving)\b/i.test(s)) return true;
  // Explicit negotiation formula / internal strategy phrasing.
  if (
    /\b(?:internal|negotiation|walk[- ]?away|max\s+discount|profit\s+margin)\b.*\b(?:rate|discount|margin|rebate|figure|target)\b/i.test(s) ||
    /\b(?:rebate|discount)\s*(?:formula|schedule|bands?)\b/i.test(s)
  ) return true;
  // Copies / internal-only / do-not-share markers — content gated for the
  // buyer team, not for bidders.  The buyer's pricing strategy and the
  // projected-bottom target words are leverage points that must never ship.
  if (
    /\b(?:internal only|confidential|not\s+for\s+(?:distribution|bidders?|suppliers?|vendors?)|do\s+not\s+(?:share|disclose|include|circulate)|between\s+us|eyes\s+only)\b/i.test(s) ||
    /\b(?:target|ceiling|floor|maximum|minimum|walk[- ]?away|anchor|expected|budget)\s+(?:price|rate|cost|fee|discount|margin)\b/i.test(s) ||
    /\b(?:net|gross|target)\s+margin\b.*\b(?:target|goal|aim|assume|[0-9][0-9.,]*%)\b/i.test(s)
  ) return true;
  // Incumbent-specific weakness / negotiation leverage mentions: critique of
  // the sitting provider written for internal framing, not for the vendor.
  if (
    /\b(?:incumbent|current\s+(?:provider|supplier|3pl|vendor|courier)|existing\s+(?:provider|supplier|3pl|vendor|courier))\b[^.\n]{0,80}?\b(?:weakn|fail(?:s|ing|ure)|\bunderperform|not\s+meeting|miss(?:es)?|breach|penalt|leverage\b|exploit|push(?:ing)?\s+back)\b/i.test(s)
  ) return true;
  return false;
}

/**
 * Convert an "Ask:" / "Question N:" draft line into a properly-punctuated
 * vendor-questionnaire question.  Strips the "Ask …:" / "Question …:" prefix
 * and appends a trailing "?" when missing.  Used both for Appendix L (Vendor
 * Questionnaire) rendering and the structured s04 extras.
 */
export function askToQuestion(raw: string): string {
  let t = String(raw || "").trim();
  t = t
    .replace(/^[•\s*>\-–]+/, "")
    .replace(/^ask\s+(?:each\s+)?(?:courier|them|the\s+(?:bidders?|vendors?|suppliers?|couriers?|carrier[s]?|partner[s]?)|you|your\s+(?:team|couriers?|company))\s*[:：]\s*/i, "")
    .replace(/^ask\s*[:：]\s*/i, "")
    .replace(/^question\s*\d*\s*[:：]\s*/i, "")
    .replace(/[;\s]+$/, "")
    .trim();
  if (!t) return "";
  if (!/[.!?]$/.test(t)) t += "?";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// ─── Prose-paragraph rewrite (Strategic Importance) ───────────────────────────

/**
 * Full-clause connectors used to chain bullets into a flowing prose paragraph.
 * Every connector ends in a comma so a capital-initial clause may follow
 * ("In addition, Liability capped at …"), preserving the verbatim quote.
 */
const PROSE_LINKERS = [
  "In addition,",
  "Moreover,",
  "In parallel,",
  "Commercially,",
  "Operationally,",
  "Critically,",
  "Furthermore,",
  "Beyond the immediate pricing,",
  "More directly,",
];

/**
 * Rewrite a list of business-risk sentences into a single flowing prose
 * paragraph.  Each sentence is kept verbatim (no invented words / numbers),
 * chained with varied connectors so the output reads as professional prose
 * rather than a bullet-point dump.  First item opens unadorned; subsequent
 * items rotate through the linker pool.
 */
export function toProseNarrative(items: string[]): string {
  const cleaned = items
    .map((s) => s.trim().replace(/^[-*>\s•]+/, "").replace(/[.!?\s]+$/, "").trim())
    .filter(Boolean);
  if (!cleaned.length) return "";
  const out: string[] = [];
  cleaned.forEach((s, i) => {
    const link = i === 0 ? "" : `${PROSE_LINKERS[(i - 1) % PROSE_LINKERS.length]} `;
    // Ensure each clause ends with a period.
    const sentence = /[.!?"/’”)]$/.test(s) ? s : `${s}.`;
    out.push(`${link}${sentence}`);
  });
  return out.join(" ");
}

// ─── Strategic-importance synthesis (vendor-facing) ───────────────────────────

/**
 * Detected strategic themes, in canonical priority order. The first three
 * themes present in the source drive the synthesized Section-1 narrative.
 * Every phrase is qualitative and vendor-facing — it never quotes an internal
 * contract clause, liability figure or negotiation tactic from the draft.
 */
const STRATEGIC_THEMES: Array<{ key: string; pattern: RegExp; phrase: string }> = [
  {
    key: "accountability",
    pattern:
      /\botif\b|on[- ]?time|service level|\bsla\b|\bpenalt|breach\b|performance|customers?\s+expect|accountab|exception|escalation|incentiv|misaligned|rush\b/i,
    phrase: "establishing transparent service-level accountability",
  },
  {
    key: "cold_chain",
    pattern:
      /cold[- ]chain|cold\s+storage|temperature|temperature[- ]?(?:controlled|integrity)|\bdose\b|dosage|radiopharm|biologic|clinical|pharma|integrity\b|quality of goods|condition integrity|degrad|labile|deliver(?:y|ies?)/i,
    phrase: "robust cold-chain risk management",
  },
  {
    key: "liability",
    pattern:
      /liabilit|capped?\s+at|exposure\b|\bclaim|insurance|indemnif|damaged|loss(?:es)?\b|no distinction|at risk/i,
    phrase: "aligned liability frameworks",
  },
  {
    key: "pricing",
    pattern:
      /mileage|fuel surcharge|inflation|price|pricing|surcharge|per[- ]shipment|wait time|rebates?\b|volume[- ]based|tiered|transparen|rate\b/i,
    phrase: "moving to transparent, market-responsive pricing structures",
  },
  {
    key: "resilience",
    pattern:
      /resilien|network|route|coverage|capacity|peak|contingenc|redundan|backup\b|multi[- ]site|failover/i,
    phrase: "strengthening network resilience and capacity planning",
  },
  {
    key: "transition",
    pattern:
      /current (?:agreement|contract|provider|supplier|incumbent)\b|incumbent\b|existing contract|expiry|expires?|transition|handover|discontinu|migrat/i,
    phrase: "ensuring continuity through a well-managed transition from the current arrangement",
  },
  {
    key: "visibility",
    pattern:
      /visibility|track|\bpod\b|report|electron|automate|digital|forecast|\bdata\b|exception reporting/i,
    phrase: "improving shipment visibility and data-driven performance reporting",
  },
];

/**
 * Synthesize the Section-1 "Strategic importance" narrative. Raw internal
 * negotiation / risk notes from the draft ("Critical Gap in Current MDS
 * Contract", "Liability capped at $100/dose", fuel-surcharge maths) are never
 * quoted verbatim. Instead the detected strategic themes are folded into a
 * polished, high-level, vendor-facing paragraph that frames operational goals,
 * SLA alignment and risk management without leaking internal leverage points.
 */
export function synthesizeStrategicImportance(sentences: string[]): string {
  const src = sentences.map((s) => (s?.trim() || "").replace(/[:]\s*$/, "")).filter(Boolean).join(" ");
  if (!src) return "";
  const found = STRATEGIC_THEMES.filter((t) => t.pattern.test(src)).slice(0, 3);
  if (!found.length) return "";
  const phrases = found.map((t) => t.phrase);
  const body =
    phrases.length === 1
      ? phrases[0]
      : phrases.length === 2
        ? `${phrases[0]} and ${phrases[1]}`
        : `${phrases[0]}, ${phrases[1]}, and ${phrases[2]}`;
  return `The strategic priority is ${body} across all operating routes.`;
}

// ─── Tier-row discount stripping ──────────────────────────────────────────────

/**
 * Strip internal "→ 3–5% discount" style pricing-structure annotations from
 * volume-tier summary rows so the rendered Section 2 shows only the
 * throughput bands, never the buyer's negotiation strategy.
 */
export function stripTierDiscounts(row: string): string {
  return row.replace(/\s*[→➜>]\s*[-–—]?\s*[0-9][0-9.,]*\s*[-–—]?\s*[0-9]*\s*%\s*(?:discount\b)?/gi, "").trimEnd();
}

// ─── Final-compilation sanitization ───────────────────────────────────────────

/**
 * Global post-processing sweep applied to every generated section body just
 * before the pack is compiled (Rule 4). Any residual bracketed internal
 * instruction — a surviving "[PLAYBOOK DEFAULT — confirm]" author note, a
 * "[TBD — confirm with insurance holder]" stub or similar raw bracket — is
 * replaced with clean, vendor-facing phrasing instead of shipping verbatim.
 */
const FINAL_BRACKET_RESOLVERS: Array<{ pattern: RegExp; replace: string }> = [
  { pattern: /\[PLAYBOOK DEFAULT[^\]]*\]/gi, replace: "as defined in the confirmed category defaults" },
  {
    pattern: /\[[^\]]{0,60}(?:confirm with (?:insurance holder|legal)|internal only|for internal use|do not share)[^\]]{0,60}\]/gi,
    replace: "commensurate with the stated scope, evidenced at proposal",
  },
];

export function sanitizeFinalText(text: string | null | undefined): string {
  if (!text) return "";
  let out = String(text);
  for (const { pattern, replace } of FINAL_BRACKET_RESOLVERS) out = out.replace(pattern, replace);
  // Playbook-hint scaffolding ("Goods-in-transit insurance minimum … — state
  // from playbook or [TBD]") is an author note, never bidder-facing. Drop the
  // " — state from playbook or [TBD]" tail so Section 10 ships the requirement
  // without a raw internal TBD stub.
  out = out.replace(/\s*[—–-]\s*state(?:ment)?\s+from\s+playbook\s+or\s+\[TBD\]\s*/gi, " ");
  out = out.replace(/\n{3,}/g, "\n\n");
  return out.replace(/[ \t]{2,}/g, " ").trim();
}

/**
 * Remove markdown bold markers ("**") from generated section text. The pack's
 * plain-text surfaces (parent/child views, outlines, exports) render `**`
 * literally, so bold emphasis is dropped at the content layer instead.
 */
export function stripBoldMarkers(text: string | null | undefined): string {
  if (!text) return "";
  return String(text).replace(/\*\*/g, "").trim();
}

// ─── Logistics-vocabulary guardrail (services domain) ─────────────────────────

/**
 * Strict domain-boundary purge for non-supply-chain (services / indirect)
 * categories. If the buyer is buying e.g. Digital Marketing, IT Managed
 * Services or Professional Services, logistics/3PL vocabulary such as OTIF,
 * contract logistics, inventory accuracy, cold chain, hazmat, radiation
 * safety, despatch/POD and fleet must never reach the pack's sections or
 * matrix. Matches are removed case-insensitively (sentence- and
 * hyphen-tolerant), then whitespace is re-collapsed.
 */
const LOGISTICS_PATTERNS: RegExp[] = [
  /\bOTIF\b/gi,
  /\bcontract\s+logistics\b/gi,
  /\binventory[- ]accuracy\b/gi,
  /\bcold[- ]chain\b/gi,
  /\bcold\s+(?:storage|room|warehouse)?\b/gi,
  /\bhazmat\b/gi,
  /\bradiation[- ]?safety\b/gi,
  /\bradiation\b/gi,
  /\bdespatch[- ]?\/?[- ]?\s*POD\b/gi,
  /\bproof[- ]of[- ]delivery\b|\bPOD\b/gi,
  /\bfleet\b/gi,
  /\b3PL\b|\bthird[- ]party\s+logistics\b/gi,
  /\bpallet[s]?\b/gi,
  /\bwarehouse\s+labou?r\b/gi,
];

export function purgeLogisticsTerms(text: string | null | undefined): string {
  if (!text) return "";
  let out = String(text);
  for (const pattern of LOGISTICS_PATTERNS) out = out.replace(pattern, " ");
  // Collapse horizontal whitespace runs only — never newlines — so the body's
  // heading/paragraph structure survives (## titles on their own lines, bullet
  // lines, blank-line separators between blocks).
  const lines = out.split("\n").map((line) => line.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/g, ""));
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").replace(/[ \t]{2,}/g, " ").trim();
}